import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BirthdayTemplates } from "@/components/birthdays/BirthdayTemplates";

const MODES = { sms_first: "Text first, email only if the text fails", sms_only: "Text only", email_only: "Email only", preferred: "Patient's preferred channel" };

const Row = ({ label, hint, children }) => (
  <div className="flex flex-col gap-2 border-t border-slate-100 py-3 sm:flex-row sm:items-center sm:justify-between">
    <div><p className="text-sm font-medium text-slate-800">{label}</p>{hint && <p className="text-xs text-slate-500">{hint}</p>}</div>
    <div className="shrink-0">{children}</div>
  </div>
);

export const BirthdaySettings = ({ initial }) => {
  const qc = useQueryClient();
  const [f, setF] = useState(initial);
  useEffect(() => { setF(initial); }, [initial]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = useMutation({ mutationFn: () => api.put("/birthdays/settings", f),
    onSuccess: () => { toast.success("Birthday settings saved"); qc.invalidateQueries({ queryKey: ["birthdays"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save") });

  return (
    <div className="space-y-6" data-testid="bday-settings">
      <div className="rounded-xl border border-slate-200 bg-white px-5 pb-2 pt-4">
        <p className="text-sm font-semibold text-[#0B1F44]">Sending rules</p>
        <Row label="Automatic birthday greetings" hint="When on, today's list is prepared at the sending time every day.">
          <Switch checked={f.enabled} onCheckedChange={set("enabled")} data-testid="bday-set-enabled" /></Row>
        <Row label="Staff approve each day's list" hint="When on, nothing is sent until someone clicks Approve. When off, greetings send automatically.">
          <Switch checked={f.approval_required} onCheckedChange={set("approval_required")} data-testid="bday-set-approval" /></Row>
        <Row label="Sending time (Central)" hint="Checked every hour, on the hour.">
          <Input type="time" step="3600" value={f.send_time} onChange={(e) => set("send_time")(e.target.value.slice(0, 2) + ":00")} className="h-9 w-32" data-testid="bday-set-time" /></Row>
        <Row label="Channel">
          <Select value={f.channel_mode} onValueChange={set("channel_mode")}>
            <SelectTrigger className="h-9 w-full sm:w-80" data-testid="bday-set-channel"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(MODES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select></Row>
        <Row label="February 29 birthdays in other years">
          <Select value={f.feb29_policy} onValueChange={set("feb29_policy")}>
            <SelectTrigger className="h-9 w-40" data-testid="bday-set-feb29"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="feb28">February 28</SelectItem><SelectItem value="mar1">March 1</SelectItem></SelectContent>
          </Select></Row>
        <div className="border-t border-slate-100 py-3">
          <Label className="text-sm font-medium text-slate-800">Clinic postal address (shown in emails)</Label>
          <p className="mb-2 text-xs text-slate-500">Required by email law (CAN-SPAM). Birthday emails won't send until this is filled in.</p>
          <Textarea rows={2} value={f.clinic_address} onChange={(e) => set("clinic_address")(e.target.value)} placeholder="[Clinic street address, Houston, TX ZIP]" data-testid="bday-set-address" />
        </div>
      </div>
      <BirthdayTemplates f={f} set={set} />
      <Button onClick={() => save.mutate()} disabled={save.isPending} className="bg-[#0B1F44] hover:bg-[#13306a]" data-testid="bday-settings-save">
        {save.isPending ? "Saving…" : "Save settings & templates"}</Button>
    </div>
  );
};
